import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Badge, Tabs, Tab } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axios from 'axios';
import { Link, useSearchParams } from 'react-router-dom';
import HelpAssistant from './HelpAssistant';
import { GuideFrame, WalkthroughFrame } from './HelpFrames';
import MyRequests from './MyRequests';
import useSectionTab from '../hooks/useSectionTab';
import { useAuth } from '../contexts/AuthContext';

// The tabs of Help & Support, at /help/<section>: the contact page, the person's own requests, the guide and the walkthroughs (the last two shown in place)
const HELP_SECTIONS = ['support', 'my-requests', 'getting-started', 'walkthroughs'];

/**
 * Help & Support: a way to reach the team (bug, idea, complaint, or a question), the requests the person has sent with the
 * conversation on each (a tab of its own, with a count of new replies), and the getting-started guide and the walkthroughs. Opened from the lifebuoy
 * at the top right.
 */
function Help() {
  const { user } = useAuth();
  // An account manager gets their own help and assistant here as in Account Management, so the two places answer the same way
  const partner = Boolean(user?.isAccountManager);
  const [activeTab, setActiveTab] = useSectionTab('/help', HELP_SECTIONS, 'support');
  // The request open in its dialog is in the address (?ticket=12), so the back button closes it and a link can open it
  const [searchParams, setSearchParams] = useSearchParams();
  const openRequestId = searchParams.get('ticket');
  const [feedbackCategory, setFeedbackCategory] = useState('OTHER');
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackLoading, setFeedbackLoading] = useState(false);
  const [myFeedback, setMyFeedback] = useState([]);
  const [myFeedbackLoading, setMyFeedbackLoading] = useState(false);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  // Requests found to have a new reply, kept while the My requests tab is open: asking again after the replies were marked seen must not hide them
  const flaggedNew = useRef(new Set());
  const visitedRequests = useRef(false);
  const isNewReply = (item) => Boolean(item.unreadReply) || flaggedNew.current.has(item.id);

  const fetchMyFeedback = async () => {
    try {
      setMyFeedbackLoading(true);
      const response = await axios.get('/api/feedback/mine');
      (response.data || []).forEach((item) => { if (item.unreadReply) flaggedNew.current.add(item.id); });
      setMyFeedback(response.data || []);
      return true;
    } catch (error) {
      console.error(error);
      return false;
    } finally {
      setMyFeedbackLoading(false);
    }
  };

  useEffect(() => { fetchMyFeedback(); }, []);

  // Replies count as seen once the requests are on screen, not merely because Help was opened. The list keeps its "New reply" marks
  // until it is fetched again, so the person can still see which ones they are
  useEffect(() => {
    if (activeTab !== 'my-requests') {
      // Leaving the tab after reading it: those replies are no longer new
      if (visitedRequests.current) {
        visitedRequests.current = false;
        flaggedNew.current.clear();
        setMyFeedback((list) => list.map((item) => ({ ...item, unreadReply: false })));
      }
      return;
    }
    visitedRequests.current = true;
    // Fetched fresh first, so a request sent from the assistant is there and the new replies are marked before they count as seen
    fetchMyFeedback().then((loaded) => loaded && axios.post('/api/feedback/mark-seen').then(() => window.dispatchEvent(new Event('ibigdo:replies-seen'))))
      .catch(() => {});
  }, [activeTab]);

  const newReplies = myFeedback.filter(isNewReply).length;
  const myRequestsTitle = (
    <span>
      My requests
      {newReplies > 0 && activeTab !== 'my-requests' && <Badge bg="danger" pill className="ms-2" data-testid="my-requests-new">{newReplies}</Badge>}
    </span>
  );

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();
    if (!feedbackMessage.trim()) {
      toast.error('Please enter your message');
      return;
    }
    setFeedbackLoading(true);
    try {
      const response = await axios.post('/api/feedback', { category: feedbackCategory, message: feedbackMessage.trim() });
      toast.success(response.data?.message || 'Thank you, we have your message');
      setFeedbackMessage('');
      setFeedbackCategory('OTHER');
      await fetchMyFeedback();
      // Show them it arrived
      setActiveTab('my-requests');
    } catch (error) {
      const errorMsg = error.response?.data || 'Failed to send your message';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to send your message');
    } finally {
      setFeedbackLoading(false);
    }
  };

  const handleAccept = async (id) => {
    try {
      setActionLoadingId(id);
      const response = await axios.post(`/api/feedback/${id}/accept`);
      toast.success(response.data?.message || 'Request closed');
      await fetchMyFeedback();
    } catch (error) {
      const errorMsg = error.response?.data || 'Failed to accept resolution';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to accept resolution');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleReject = async (id) => {
    const comment = window.prompt('Optional: tell us what is still wrong (or leave blank)');
    if (comment === null) return; // cancelled
    try {
      setActionLoadingId(id);
      const response = await axios.post(`/api/feedback/${id}/reject`, { comment });
      toast.success(response.data?.message || 'Request reopened');
      await fetchMyFeedback();
    } catch (error) {
      const errorMsg = error.response?.data || 'Failed to reopen the request';
      toast.error(typeof errorMsg === 'string' ? errorMsg : 'Failed to reopen the request');
    } finally {
      setActionLoadingId(null);
    }
  };

  return (
    <Container>
      <Tabs activeKey={activeTab} onSelect={(k) => setActiveTab(k)} className="mb-4" mountOnEnter>
      <Tab eventKey="support" title="Ask & contact">
      <HelpAssistant
        embedded
        partner={partner}
        apiBase={partner ? '/api/account-manager/assistant' : '/api/assistant'}
      />
      <Row className="g-3 mb-4">
        <Col md={4}>
          <Card className="h-100">
            <Card.Body>
              <h5><i className="bi bi-book me-2" />Getting started</h5>
              <p className="text-muted small mb-2">
                One purchase, followed from the vendor to the ledger. It shows how the parts of iBigDo fit together.
              </p>
              <Button variant="link" className="p-0" onClick={() => setActiveTab('getting-started')}>Read the Getting Started guide</Button>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100">
            <Card.Body>
              <h5><i className="bi bi-play-circle me-2" />Watch a walkthrough</h5>
              <p className="text-muted small mb-2">
                From quotation to cash in hand, in 16 captioned screens: quote a customer, send it, bill it, and see the payment reach the books.
              </p>
              <Link to="/help/walkthroughs?story=quote-to-cash">Watch: quotation to cash</Link>
              <p className="text-muted small mt-3 mb-2">
                Setting up your items, in 9 screens: add a material from the Global Material Master with the stock you already hold, then a product.
              </p>
              <Link to="/help/walkthroughs?story=set-up-your-items">Watch: set up your items</Link>
              <p className="text-muted small mt-3 mb-2">
                Selling online, in 21 screens: set up your store, payments and shipping, publish products, a buyer orders, and your staff invoice, send, deliver, take a return and read the store report.
              </p>
              <Link to="/help/walkthroughs?story=sell-online">Watch: sell online</Link>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card className="h-100">
            <Card.Body>
              <h5><i className="bi bi-question-circle me-2" />Help on every screen</h5>
              <p className="text-muted small mb-0">
                Every tab has a <i className="bi bi-question-circle" /> icon next to its name. Click it for what that screen is for and how to use it, without leaving the page.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row>
        <Col lg={8} xl={7}>
          <Card className="mb-4">
            <Card.Header>
              <h5 className="mb-0">Contact Support</h5>
            </Card.Header>
            <Card.Body>
              <p className="text-muted small">
                Report a bug, raise a concern, share an idea, or ask how to do something. Our team replies here, and you can see every
                reply under the My requests tab.
              </p>
              <Form onSubmit={handleFeedbackSubmit}>
                <Form.Group className="mb-3" controlId="help-category">
                  <Form.Label>What is this about?</Form.Label>
                  <Form.Select value={feedbackCategory} onChange={(e) => setFeedbackCategory(e.target.value)}>
                    <option value="OTHER">I need help with something</option>
                    <option value="BUG">Something is not working (bug)</option>
                    <option value="IDEA">I have an idea</option>
                    <option value="COMPLAINT">I have a complaint</option>
                  </Form.Select>
                </Form.Group>

                <Form.Group className="mb-3" controlId="help-message">
                  <Form.Label>Message</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    value={feedbackMessage}
                    onChange={(e) => setFeedbackMessage(e.target.value)}
                    maxLength={5000}
                    required
                    placeholder="What went wrong, what would you like to see, or how can we help?"
                  />
                  <Form.Text className="text-muted">{feedbackMessage.length}/5000</Form.Text>
                </Form.Group>

                <Button variant="primary" type="submit" disabled={feedbackLoading}>
                  {feedbackLoading ? 'Sending...' : 'Send'}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

      </Row>
      </Tab>
      <Tab eventKey="my-requests" title={myRequestsTitle}>
        <MyRequests
          items={myFeedback}
          loading={myFeedbackLoading}
          onRefresh={fetchMyFeedback}
          isNewReply={isNewReply}
          openId={openRequestId}
          onOpen={(id) => setSearchParams({ ticket: String(id) })}
          onClose={() => setSearchParams({})}
          onAccept={handleAccept}
          onReject={handleReject}
          actionLoadingId={actionLoadingId}
        />
      </Tab>
      <Tab eventKey="getting-started" title="Getting started">
        <GuideFrame />
      </Tab>
      <Tab eventKey="walkthroughs" title="Walkthroughs">
        <WalkthroughFrame />
      </Tab>
      </Tabs>

      <nav aria-label="Legal" className="d-flex flex-wrap gap-3 small border-top pt-3 mb-4">
        <Link to="/terms">Terms of Service</Link>
        <Link to="/privacy">Privacy Policy</Link>
        <Link to="/refund-policy">Refund &amp; Cancellation</Link>
        <Link to="/security">Security &amp; Data</Link>
      </nav>
    </Container>
  );
}

export default Help;
